import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  ArrowLeft, FolderOpen, FileText, Plus, ChevronRight, Download,
  Mail, Phone, MapPin, Paperclip, Loader2,
} from 'lucide-react';
import { authedUrl } from '../../lib/authSession';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  draft: { color: 'bg-gray-100 text-gray-700', label: 'Draft' },
  intake_pending: { color: 'bg-sky-100 text-sky-700', label: 'Awaiting Client' },
  in_progress: { color: 'bg-blue-100 text-blue-700', label: 'In Progress' },
  pending_client_signature: { color: 'bg-yellow-100 text-yellow-700', label: 'Pending Signature' },
  pending_supervisor_review: { color: 'bg-orange-100 text-orange-700', label: 'Pending Review' },
  submitted_to_insurer: { color: 'bg-purple-100 text-purple-700', label: 'Submitted' },
  underwriting: { color: 'bg-indigo-100 text-indigo-700', label: 'Underwriting' },
  approved: { color: 'bg-teal-100 text-teal-700', label: 'Approved' },
  issued: { color: 'bg-cyan-100 text-cyan-700', label: 'Issued' },
  delivered: { color: 'bg-green-100 text-green-700', label: 'Delivered' },
  closed: { color: 'bg-emerald-100 text-emerald-700', label: 'Closed' },
  compliance_hold: { color: 'bg-red-100 text-red-700', label: 'Compliance Hold' },
};

const fmtSize = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const ClientFolder = ({ agent, client, onNavigate }) => {
  const [files, setFiles] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  const clientId = client?.id;

  const fetchFolder = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const [casesRes, docsRes] = await Promise.all([
        fetch(`${API_URL}/api/compliance/cases?client_id=${clientId}`),
        fetch(`${API_URL}/api/compliance/clients/${clientId}/uploaded-documents`),
      ]);
      if (casesRes.ok) setFiles(await casesRes.json());
      if (docsRes.ok) setDocuments(await docsRes.json());
    } catch (err) {
      console.error('Failed to load client folder:', err);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { fetchFolder(); }, [fetchFolder]);

  const download = (id) => {
    window.open(authedUrl(`${API_URL}/api/compliance/uploaded-documents/${id}/download`), '_blank', 'noopener');
  };

  if (!client) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">No client selected.</p>
        <Button onClick={() => onNavigate('clients')} className="mt-4 bg-[#0077B6] hover:bg-[#005A8C]">
          Back to Clients
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="client-folder">
      {/* Back */}
      <button
        onClick={() => onNavigate('clients')}
        className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-[#003D5C]"
        data-testid="client-folder-back"
      >
        <ArrowLeft className="h-4 w-4" /> Back to Clients
      </button>

      {/* Client header */}
      <Card className="bg-gradient-to-br from-[#003D5C] to-[#0077B6] text-white border-0">
        <CardContent className="p-5 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="w-14 h-14 bg-[#D4AF37] rounded-xl flex items-center justify-center text-[#003D5C] font-bold text-2xl flex-shrink-0">
            {client.full_name?.charAt(0) || 'C'}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <FolderOpen className="h-5 w-5 text-[#D4AF37]" />
              <h1 className="text-xl sm:text-2xl font-bold truncate">{client.full_name}</h1>
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-blue-100">
              {client.email && <span className="inline-flex items-center gap-1"><Mail className="h-3.5 w-3.5" />{client.email}</span>}
              {client.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{client.phone}</span>}
              {(client.city || client.province) && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{[client.city, client.province].filter(Boolean).join(', ')}</span>}
            </div>
          </div>
          <Button
            onClick={() => onNavigate('new-case', { clientId: client.id })}
            className="bg-[#D4AF37] hover:bg-[#B8960C] text-[#003D5C] font-semibold w-full sm:w-auto"
            data-testid="client-folder-new-file"
          >
            <Plus className="h-4 w-4 mr-1" /> New File
          </Button>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#003D5C]" /></div>
      ) : (
        <>
          {/* Files */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <FileText className="h-5 w-5 text-[#003D5C]" />
              <h2 className="text-lg font-bold text-[#003D5C]">Files</h2>
              <span className="text-sm text-gray-400">({files.length})</span>
            </div>
            <Card>
              <CardContent className="p-0">
                {files.length === 0 ? (
                  <div className="text-center py-10">
                    <FileText className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 text-sm">No files yet for this client.</p>
                    <Button
                      onClick={() => onNavigate('new-case', { clientId: client.id })}
                      className="mt-4 bg-[#0077B6] hover:bg-[#005A8C]"
                    >
                      <Plus className="h-4 w-4 mr-1" /> Create First File
                    </Button>
                  </div>
                ) : (
                  <div className="divide-y" data-testid="client-folder-files">
                    {files.map((f) => {
                      const sc = STATUS_CONFIG[f.status] || { color: 'bg-gray-100 text-gray-700', label: f.status };
                      return (
                        <button
                          key={f.id}
                          onClick={() => onNavigate('case-detail', { caseId: f.id })}
                          className="w-full text-left p-4 flex items-center gap-4 hover:bg-gray-50 transition-colors"
                          data-testid={`client-folder-file-${f.id}`}
                        >
                          <FileText className="h-5 w-5 text-[#0077B6] flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-[#003D5C]">{f.case_number}</p>
                            <p className="text-sm text-gray-500 truncate">
                              {f.product_type || 'No product'}{f.carrier ? ` · ${f.carrier}` : ''}
                              {f.coverage_amount ? ` · $${Number(f.coverage_amount).toLocaleString()}` : ''}
                            </p>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${sc.color}`}>{sc.label}</span>
                          <span className="text-xs text-gray-400 hidden sm:inline">{f.updated_at ? new Date(f.updated_at).toLocaleDateString() : ''}</span>
                          <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0" />
                        </button>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Documents */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Paperclip className="h-5 w-5 text-[#003D5C]" />
              <h2 className="text-lg font-bold text-[#003D5C]">Uploaded Documents</h2>
              <span className="text-sm text-gray-400">({documents.length})</span>
            </div>
            <Card>
              <CardContent className="p-4">
                {documents.length === 0 ? (
                  <p className="text-gray-500 text-center py-6 text-sm">
                    No documents uploaded yet. Open a file above to upload scans, IDs, or signed PDFs.
                  </p>
                ) : (
                  <div className="space-y-2" data-testid="client-folder-documents">
                    {documents.map((doc) => (
                      <div key={doc.id} className="flex items-center gap-3 border rounded-lg p-3" data-testid={`client-folder-doc-${doc.id}`}>
                        <FileText className="h-5 w-5 text-[#003D5C] flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{doc.label || doc.original_filename}</p>
                          <p className="text-xs text-gray-500 truncate">
                            {doc.case_number ? <span className="text-[#0077B6] font-medium">{doc.case_number}</span> : null}
                            {doc.case_number ? ' · ' : ''}
                            {doc.original_filename} · {fmtSize(doc.size)}
                            {doc.uploaded_by_name ? ` · by ${doc.uploaded_by_name}` : ''}
                            {doc.created_at ? ` · ${new Date(doc.created_at).toLocaleDateString()}` : ''}
                          </p>
                        </div>
                        <Button size="sm" variant="outline" onClick={() => download(doc.id)} data-testid={`client-folder-download-${doc.id}`}>
                          <Download className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
};

export default ClientFolder;
